import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { SearchFilters } from '@lidea/search/shared/ui-filters';
import { PfTypeahead } from '@lidea/search/shared/ui-pf-typeahead';
import actions from '../../../ui-pf-typeahead/src/lib/modal-actions.module.css';
import { useFilters, useFilterStore } from '@lidea/shared/data-access/core';
import { Filters } from '@lidea/shared/util/types';
import { arrowBack, close } from 'ionicons/icons';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './filters-modal.module.css';

export interface FiltersModalProps {
  isOpen: boolean;
  onWillDismiss?: () => void;
  modalClassName?: string;
}

type TypeaheadState = {
  filter: keyof Filters | null;
  isOpen: boolean;
};

const INITIAL_TYPEAHEAD: TypeaheadState = { filter: null, isOpen: false };

export function FiltersModal({
  isOpen,
  onWillDismiss,
  modalClassName,
}: FiltersModalProps) {
  const { t } = useTranslation(['common', 'plot'], { useSuspense: false });
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [typeahead, setTypeahead] = useState<TypeaheadState>(INITIAL_TYPEAHEAD);

  const selectFilter = useFilterStore((state) => state.selectFilter);
  const resetFilters = useFilterStore((state) => state.resetFilters);
  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const { filtersAsOptions, isSuccess } = useFilters();

  const activeFilter = typeahead.isOpen ? typeahead.filter : null;
  const inTypeahead = Boolean(activeFilter);

  const closeTypeahead = useCallback(() => {
    setTypeahead(INITIAL_TYPEAHEAD);
  }, []);

  const closeModal = useCallback(() => {
    modalRef.current?.dismiss();
  }, []);

  const handleHeaderClose = useCallback(() => {
    if (inTypeahead) {
      closeTypeahead();
      return;
    }
    onWillDismiss?.();
  }, [closeTypeahead, inTypeahead, onWillDismiss]);

  const handleFilterClick = useCallback((filter: keyof Filters) => {
    setTypeahead({ isOpen: true, filter });
  }, []);

  const handleResetFilters = useCallback(() => {
    resetFilters();
    closeModal();
  }, [closeModal, resetFilters]);

  const handleSelectionChange = useCallback(
    (items: string[]) => {
      if (!activeFilter) {
        return;
      }
      selectFilter({ filter: activeFilter, value: items });
      closeTypeahead();
    },
    [activeFilter, closeTypeahead, selectFilter]
  );

  const modalClass = [styles.modal, modalClassName].filter(Boolean).join(' ');
  const typeaheadTitle = activeFilter
    ? String(t(`plot:${activeFilter}`))
    : '';
  const typeaheadOptions =
    activeFilter && filtersAsOptions
      ? filtersAsOptions[activeFilter]?.options ?? []
      : [];

  return (
    <IonModal
      ref={modalRef}
      className={modalClass}
      isOpen={isOpen}
      onWillDismiss={onWillDismiss}
    >
      <IonHeader>
        <IonToolbar>
          {inTypeahead ? (
            <IonButtons slot="start">
              <IonButton onClick={closeTypeahead}>
                <IonIcon aria-label={t('common:back')} icon={arrowBack} slot="icon-only" />
              </IonButton>
            </IonButtons>
          ) : null}
          <IonTitle>
            {inTypeahead ? typeaheadTitle : t('common:filters')}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleHeaderClose}>
              <IonIcon
                aria-label={t('common:close')}
                icon={close}
                slot="icon-only"
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className={styles.modalContent}>
        <div
          className={
            inTypeahead ? styles.modalBodyTypeahead : styles.modalBody
          }
        >
          {inTypeahead ? (
            <PfTypeahead
              key={activeFilter}
              items={typeaheadOptions}
              selectedItems={selectedFilters[activeFilter!] ?? []}
              onSelectionChange={handleSelectionChange}
            />
          ) : isSuccess ? (
            <SearchFilters
              filters={filtersAsOptions}
              selectedFilters={selectedFilters}
              onFilterClick={handleFilterClick}
            />
          ) : null}
        </div>
      </IonContent>

      {!inTypeahead ? (
        <IonFooter className={styles.modalFooter}>
          <IonToolbar>
            <div className={styles.modalFooterInner}>
              <div className={actions.actionsCentered}>
                <button
                  className={actions.btnFilled}
                  type="button"
                  onClick={closeModal}
                >
                  {t('common:validate')}
                </button>
                <button
                  className={actions.btnReset}
                  type="button"
                  onClick={handleResetFilters}
                >
                  {t('common:reset')}
                </button>
              </div>
            </div>
          </IonToolbar>
        </IonFooter>
      ) : null}
    </IonModal>
  );
}

export default FiltersModal;
